import ReactSelect, { components } from 'react-select'

import Icon from '../Icon'
import classNames from 'classnames'
import CircleLoader from '../CircleLoader'

import './option.scss'
import './select.scss'

const Option = props => {
    const { data, isSelected, isFocused, label } = props
    const { showPrice } = props.selectProps
    const { icon, secondaryText, rateName, amount, currencySymbol } = data

    return (
        <components.Option {...props}>
            <div
                className={classNames('option', {
                    'is-selected': isSelected,
                    'is-focused': isFocused,
                })}>
                {icon && (
                    <div className="option__icon">
                        <Icon type={icon} size="xs">
                            {icon}
                        </Icon>
                    </div>
                )}
                <div
                    className={classNames('option__text ', {
                        'option__text-wrapper': !!secondaryText,
                        'w-100': rateName,
                    })}>
                    <p
                        className={classNames({
                            'option__main-text': !!secondaryText,
                        })}>
                        {label}
                    </p>
                    {rateName && (
                        <div className="d-flex justify-content-between w-100 ">
                            <p>{rateName}</p>
                            {showPrice && <div>{showPrice(amount, currencySymbol)}</div>}
                        </div>
                    )}
                    {secondaryText && (
                        <div className="option__secondary-text">
                            {secondaryText.map((text, index) => (
                                <p key={index}>{text}</p>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </components.Option>
    )
}

const DropdownIndicator = ({ selectProps, ...props }) => {
    if (!selectProps.arrow) return null
    return <components.DropdownIndicator {...props} />
}

const Control = ({ children, selectProps, ...props }) => {
    const { icon, size } = selectProps

    if (icon) {
        const getIconSize = () => {
            if (size === 'small') return 'xs'
            if (size === 'large') return 'md'

            return 'sm'
        }

        return (
            <components.Control {...props}>
                <div className="select__icon">
                    <Icon type={icon} size={getIconSize()} />
                </div>
                {children}
            </components.Control>
        )
    }

    return <components.Control {...props}>{children}</components.Control>
}

const LoadingIndicator = () => (
    <div className="select__loading-indicator">
        <CircleLoader size="small" />
    </div>
)

export default function Select({
    components: propComponents,
    size,
    scaleEffect,
    borders = true,
    icon,
    noOptionsCustomMessage,
    enterTermMessage,
    arrow = true,
    isTabletOrDesktop,
    fontFamily = 'Roboto',
    ariaLabel,
    inputId,
    ...reactSelectProps
}) {
    const getControlHeight = () => {
        switch (size) {
            case 'small':
                return '40px'
            case 'large':
                return isTabletOrDesktop ? '88px' : '68px'
            default:
                return '50px'
        }
    }
    const getFontSize = () => {
        switch (size) {
            case 'large':
                return isTabletOrDesktop ? '20px' : '16px'
            default:
                return '16px'
        }
    }

    const props = {
        ...reactSelectProps,
        className: `select ${reactSelectProps.className}`,
        classNamePrefix: 'select',
        icon,
        arrow,
        size,
        inputId: inputId,
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        noOptionsMessage: ({ inputValue }) => {
            if (enterTermMessage && inputValue === '') return enterTermMessage

            return noOptionsCustomMessage ? noOptionsCustomMessage : <T>select.no-results</T>
        },
        styles: {
            valueContainer: provided => ({
                ...provided,
                fontSize: getFontSize(),
                padding: '2px 12px',
                position: 'unset',
                lineHeight: '24px',
            }),
            control: provided => {
                const borderColor = !borders ? 'transparent' : provided.borderColor
                const hoverBorderColor = !borders ? 'transparent' : provided['&:hover'].borderColor

                return {
                    ...provided,
                    boxShadow: 'none',
                    fontFamily,
                    height: reactSelectProps.isMulti ? false : getControlHeight(),
                    minHeight: getControlHeight(),
                    borderColor,
                    borderRadius: '6px',
                    '&:hover': { borderColor: hoverBorderColor },
                }
            },
            option: provided => ({ ...provided, padding: 0 }),
            container: (provided, state) => {
                const getScaleEffectStyles = () => {
                    if (scaleEffect && state.isFocused)
                        return {
                            transform: 'scale(1.1) translateY(-20px)',
                        }

                    if (scaleEffect && !state.isFocused) {
                        return { transform: 'scale(1)' }
                    }
                }
                return {
                    ...provided,
                    transition: 'transform .2s ease-in-out',
                    ...getScaleEffectStyles(),
                }
            },
            input: provided => ({
                ...provided,
                fontFamily: 'inherit',
            }),
            placeholder: provided => ({
                ...provided,
                color: size === 'large' ? '#552373' : provided.color,
            }),
        },
        components: {
            Option,
            IndicatorSeparator: null,
            DropdownIndicator,
            Control,
            LoadingIndicator,
            ...propComponents,
        },
        theme: theme => ({
            ...theme,
            colors: {
                ...theme.colors,
                primary: '#b80e80',
                neutral30: '#000',
            },
            spacing: {
                ...theme.spacing,
            },
        }),
    }

    return <ReactSelect {...props} />
}
